// System Owner Dashboard - Admin Portal
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, tenantAPI } from '../../services/api';
import api from '../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import CreateTenantDialog from '../../components/admin/CreateTenantDialog';
import TenantProfileDialog from '../../components/admin/TenantProfileDialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import {
  Building2,
  Users,
  BarChart3,
  Settings,
  Plus,
  MoreVertical,
  Eye,
  Pause,
  Play,
  LogOut,
  Gavel,
  Trash2,
  Upload,
  Shield,
  Pencil,
  Loader2,
  LifeBuoy,
  Database,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  Link2,
} from 'lucide-react';
import { useConfirm } from '../../components/common/ConfirmDialog';


// ── Repository Force Sync card moved to MonitoringPage → Repository Sync tab ──


const SystemDashboardPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const { data: systemData, isLoading: loading } = useQuery({
    queryKey: QK.systemDashboard(),
    queryFn: async () => {
      const [dashRes, tenantsRes] = await Promise.all([
        dashboardAPI.getSystemDashboard(),
        tenantAPI.list(),
      ]);
      return { dashboardData: dashRes.data, tenants: tenantsRes.data };
    },
    onError: () => toast.error('Failed to load system dashboard'),
  });
  const dashboardData = systemData?.dashboardData ?? null;
  const tenants       = systemData?.tenants       ?? [];

  const { data: openTickets = [] } = useQuery({
    queryKey: QK.adminOpenTickets(),
    queryFn: () => api.get('/chatbot/tickets?status=open').then(r => r.data),
  });
  const openTicketCount = openTickets.length;

  const invalidate = () => qc.invalidateQueries({ queryKey: QK.systemDashboard() });

  const handleSuspendTenant = async (tenantId) => {
    try {
      await tenantAPI.suspend(tenantId);
      toast.success('Tenant suspended');
      invalidate();
    } catch (error) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivateTenant = async (tenantId) => {
    try {
      await tenantAPI.activate(tenantId);
      toast.success('Tenant activated');
      invalidate();
    } catch (error) {
      toast.error('Failed to activate tenant');
    }
  };

  const handleTenantCreated = (newTenant) => {
    setCreateDialogOpen(false);
    invalidate();
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setProfileDialogOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setEditForm({
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      website: tenant.website || '',
      address_street: tenant.address_street || '',
      address_city: tenant.address_city || '',
      address_state: tenant.address_state || '',
      address_zip: tenant.address_zip || '',
      subscription_tier: tenant.subscription_tier || 'starter',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name?.trim()) {
      toast.error('Tenant name is required');
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/tenants/${selectedTenant.id}`, editForm);
      toast.success('Tenant updated successfully');
      setEditDialogOpen(false);
      invalidate();
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!await confirm({ title: `Are you sure you want to DELETE "${tenant.name}"?\n\nThis will permanently delete:\n- All users\n- All horses\n- All CRM entities\n- All activities\n- All ownership records\n\nThis action CANNOT be undone!`, destructive: true })) return;

    try {
      await tenantAPI.delete(tenant.id);
      toast.success('Tenant deleted successfully');
      invalidate();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Failed to delete tenant');
    }
  };

  const handleImpersonate = async (tenant) => {
    try {
      const response = await api.post(`/impersonation/start/${tenant.id}`);
      const impersonationData = response.data;
      
      // Store impersonation info
      localStorage.setItem('impersonation_id', impersonationData.impersonation_id);
      localStorage.setItem('original_user', JSON.stringify(user));
      // Save original JWT and swap in the scoped impersonation token
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('token', impersonationData.access_token);
      
      // Update auth context with impersonated user
      localStorage.setItem('user', JSON.stringify(impersonationData.user));

      // Update the service worker offline cache with the impersonation token.
      // Without this, mobile devices that go offline/background during impersonation
      // would restore the system owner's cached token on reconnect, causing
      // tenant_id = null in API calls and "No data found" in reports.
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'WARM_CACHE',
          token: impersonationData.access_token,
          baseUrl: window.location.origin || '',
        });
      }
      
      toast.success(`Now viewing as ${tenant.name}`);
      
      // Reload to apply impersonation
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error('Failed to start impersonation');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-background" data-testid="system-dashboard">
      {/* Header */}
      <header className="bg-primary text-white px-6 py-4">
        <div className="px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white flex items-center justify-center rounded p-1">
              <img src="/doors-gray-logo.png" alt="Green Door" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-serif text-xl font-semibold">Green Door</h1>
              <p className="text-xs text-white/70">System Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-6 space-y-6">
        {/* Welcome */}
        <div>
          <h2 className="font-serif text-3xl font-semibold">System Overview</h2>
          <p className="text-muted-foreground mt-1">
            Manage tenants, monitor usage, and configure the platform.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card data-testid="stat-tenants">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Tenants
                  </p>
                  <p className="font-serif text-3xl font-semibold mt-1">{stats.total_tenants || 0}</p>
                </div>
                <Building2 className="w-8 h-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-active-tenants">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Active
                  </p>
                  <p className="font-serif text-3xl font-semibold mt-1 text-green-600">
                    {stats.active_tenants || 0}
                  </p>
                </div>
                <Play className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Users
                  </p>
                  <p className="font-serif text-3xl font-semibold mt-1">{stats.total_users || 0}</p>
                </div>
                <Users className="w-8 h-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-horses">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Horses
                  </p>
                  <p className="font-serif text-3xl font-semibold mt-1">{stats.total_horses || 0}</p>
                </div>
                <HorseshoeIcon className="w-8 h-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-opportunities">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Sale Opps
                  </p>
                  <p className="font-serif text-3xl font-semibold mt-1">
                    {stats.total_sale_opportunities || 0}
                  </p>
                </div>
                <Gavel className="w-8 h-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <Button className="btn-primary" onClick={() => setCreateDialogOpen(true)} data-testid="create-tenant-btn">
            <Plus className="w-4 h-4 mr-2" /> New Tenant
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/data-import')} data-testid="data-import-btn">
            <Upload className="w-4 h-4 mr-2" /> Data Import
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/sales')} data-testid="manage-sales-btn">
            <Gavel className="w-4 h-4 mr-2" /> Manage Sales
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/monitoring')} data-testid="monitoring-btn">
            <BarChart3 className="w-4 h-4 mr-2" /> Monitoring
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/system-users')} data-testid="system-users-btn">
            <Users className="w-4 h-4 mr-2" /> System Users
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/audit')} data-testid="audit-trail-btn">
            <Shield className="w-4 h-4 mr-2" /> Audit Trail
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/config')} data-testid="system-settings-btn">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          <Button variant="outline" className="relative" onClick={() => navigate('/admin/support')} data-testid="support-tickets-btn">
            <LifeBuoy className="w-4 h-4 mr-2" /> Support Center
            {openTicketCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 pointer-events-none">
                {openTicketCount > 99 ? '99+' : openTicketCount}
              </span>
            )}
          </Button>
        </div>

        {/* Tenants Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Tenants</CardTitle>
            <CardDescription>All consignors using Green Door</CardDescription>
          </CardHeader>
          <CardContent>
            <Table className="table-heritage">
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                      <p className="text-muted-foreground">No tenants yet</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  tenants.map((tenant) => (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{tenant.name}</p>
                            <p className="text-xs text-muted-foreground">{tenant.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tenant.status === 'active' ? (
                          <Badge className="badge-active">Active</Badge>
                        ) : tenant.status === 'pending_setup' ? (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1 w-fit">
                            <AlertTriangle className="w-3 h-3" /> Pending Setup
                          </Badge>
                        ) : tenant.status === 'suspended' ? (
                          <Badge className="badge-warning">Suspended</Badge>
                        ) : !tenant.subscription_active && tenant.status === 'active' ? (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 w-fit">Cancelled</Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm capitalize">{tenant.subscription_tier || 'Starter'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewTenant(tenant)}>
                              <Eye className="w-4 h-4 mr-2" /> View Profile
                            </DropdownMenuItem>
                            {tenant.status === 'pending_setup' && (
                              <>
                              <DropdownMenuItem
                                className="text-green-700 font-medium"
                                onClick={async () => {
                                  if (!await confirm({ title: `Confirm & activate account for ${tenant.name}? This will create their admin user and send them a welcome email.`, destructive: true })) return;
                                  try {
                                    await api.post(`/billing/confirm-pending-tenant/${tenant.id}`);
                                    toast.success(`${tenant.name} confirmed and activated — welcome email sent`);
                                    invalidate();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to confirm tenant');
                                  }
                                }}
                              >
                                <UserCheck className="w-4 h-4 mr-2" /> Confirm & Activate
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-blue-700"
                                onClick={async () => {
                                  if (!await confirm({ title: `Reject setup assistance for ${tenant.name} and activate their account without it? They will receive a welcome email immediately.`, destructive: true })) return;
                                  try {
                                    await api.post(`/billing/confirm-pending-tenant/${tenant.id}?reject_setup=true`);
                                    toast.success(`${tenant.name} activated — setup assistance declined`);
                                    invalidate();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to activate tenant');
                                  }
                                }}
                              >
                                <UserCheck className="w-4 h-4 mr-2" /> Reject Assistance & Activate
                              </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleImpersonate(tenant)}>
                              <Eye className="w-4 h-4 mr-2" /> Impersonate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-blue-700"
                              onClick={async () => {
                                try {
                                  const res = await api.post(`/billing/admin/checkout-link/${tenant.id}`);
                                  const { checkout_url, email } = res.data;
                                  window.open(checkout_url, '_blank');
                                  toast.success(`Checkout link opened for ${email}`);
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to generate checkout link');
                                }
                              }}
                            >
                              <Link2 className="w-4 h-4 mr-2" /> Set Up Subscription
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditTenant(tenant)}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            {tenant.status === 'active' ? (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleSuspendTenant(tenant.id)}
                              >
                                <Pause className="w-4 h-4 mr-2" /> Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleActivateTenant(tenant.id)}>
                                <Play className="w-4 h-4 mr-2" /> Activate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteTenant(tenant)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete Tenant
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Dialogs */}
      <CreateTenantDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleTenantCreated}
      />

      <TenantProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        tenant={selectedTenant}
      />

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Tenant Name *</Label>
                <Input
                  data-testid="edit-tenant-name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Tenant name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  data-testid="edit-tenant-email"
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="info@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  data-testid="edit-tenant-phone"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Website</Label>
                <Input
                  data-testid="edit-tenant-website"
                  value={editForm.website || ''}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Street Address</Label>
                <Input
                  data-testid="edit-tenant-street"
                  value={editForm.address_street || ''}
                  onChange={(e) => setEditForm({ ...editForm, address_street: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  data-testid="edit-tenant-city"
                  value={editForm.address_city || ''}
                  onChange={(e) => setEditForm({ ...editForm, address_city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  data-testid="edit-tenant-state"
                  value={editForm.address_state || ''}
                  onChange={(e) => setEditForm({ ...editForm, address_state: e.target.value })}
                  placeholder="State"
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input
                  data-testid="edit-tenant-zip"
                  value={editForm.address_zip || ''}
                  onChange={(e) => setEditForm({ ...editForm, address_zip: e.target.value })}
                  placeholder="ZIP"
                />
              </div>
              <div className="space-y-2">
                <Label>Subscription Tier</Label>
                <Select
                  value={editForm.subscription_tier || 'starter'}
                  onValueChange={(v) => setEditForm({ ...editForm, subscription_tier: v })}
                >
                  <SelectTrigger data-testid="edit-tenant-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-tenant-btn">
              {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemDashboardPage;
